'use client';

import React, { useState } from 'react';
import { apiContactForm } from '@/apis/user';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';

interface ContactFormComponentProps {
    component: ComponentNode;
    context?: {
        domain?: string;
    };
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactFormComponent: React.FC<ContactFormComponentProps> = ({ component, context }) => {
    const { styles, customClassName, buttonText } = component;
    const inlineStyles = convertStylesToInline(styles);

    const [name, setName] = useState('');
    const [number, setNumber] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isPending, setIsPending] = useState(false);
    const [status, setStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });

    const submitLabel = typeof buttonText === 'string' && buttonText.trim().length > 0 ? buttonText : 'Send Message';

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!name.trim() || !email.trim() || !message.trim()) {
            setStatus({ type: 'error', message: 'Please fill in your name, email, and message.' });
            return;
        }
        if (!emailRegex.test(email.trim())) {
            setStatus({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }

        setIsPending(true);
        setStatus({ type: null, message: '' });

        try {
            const domain = context?.domain || '';
            const response = await apiContactForm(domain, name.trim(), number.trim(), email.trim(), message.trim());

            if (response?.message?.toString().toLowerCase().includes('success') || response?.status) {
                setStatus({ type: 'success', message: 'Message sent successfully!' });
                setName('');
                setNumber('');
                setEmail('');
                setMessage('');
            } else {
                setStatus({ type: 'error', message: response?.message?.toString() || 'Failed to send message. Please try again later.' });
            }
        } catch (error) {
            setStatus({ type: 'error', message: error instanceof Error ? error.message : 'Failed to send message. Please try again later.' });
        } finally {
            setIsPending(false);
        }
    };

    const inputStyles: React.CSSProperties = {
        width: '100%',
        border: '1px solid #e2e8f0',
        outline: 'none',
        padding: '14px 18px',
        fontSize: '14px',
        color: '#0f172a',
        backgroundColor: '#ffffff',
        borderRadius: '999px',
        boxSizing: 'border-box',
    };

    const textareaStyles: React.CSSProperties = {
        ...inputStyles,
        borderRadius: '18px',
        minHeight: '110px',
        resize: 'vertical',
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={customClassName}
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', width: '100%', ...inlineStyles }}
        >
            <input type="text" placeholder="Enter name..." value={name} onChange={(e) => setName(e.target.value)} disabled={isPending} style={inputStyles} />
            <input type="text" placeholder="Enter number..." value={number} onChange={(e) => setNumber(e.target.value)} disabled={isPending} style={inputStyles} />
            <input type="email" placeholder="Enter email..." value={email} onChange={(e) => setEmail(e.target.value)} disabled={isPending} style={inputStyles} />
            <textarea placeholder="Say something..." value={message} onChange={(e) => setMessage(e.target.value)} disabled={isPending} style={textareaStyles} />
            <button
                type="submit"
                disabled={isPending}
                style={{
                    backgroundColor: '#f97316',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '999px',
                    padding: '14px 28px',
                    cursor: isPending ? 'not-allowed' : 'pointer',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    opacity: isPending ? 0.75 : 1,
                }}
            >
                {isPending ? 'Sending...' : submitLabel}
            </button>
            {status.message ? (
                <p style={{ margin: 0, fontSize: '13px', color: status.type === 'success' ? '#15803d' : '#dc2626' }}>
                    {status.message}
                </p>
            ) : null}
        </form>
    );
};

export default ContactFormComponent;
